import { useEffect, useRef, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { Alert, Button, Icon, Spinner } from '@economic/taco';
import internalApiClient from '~/app/shared/util/api/internalApiClient';
import { SyncDataItem } from './types';

interface Props {
    children: string | JSX.Element;
    handleOnAcceptTransfer: () => void;
    trackEvent: (event: string, trackingData?: object) => void;
}

export default function TransferDataInProgress({ handleOnAcceptTransfer, trackEvent, children }: Props) {
    const initialSyncDataItems: SyncDataItem[] = [
        { name: t`Customers`, isSynced: false, viewOrder: 1, synOrder: 1 },
        { name: t`Customer Groups`, isSynced: false, viewOrder: 4, synOrder: 2 },
        { name: t`Employees`, isSynced: false, viewOrder: 7, synOrder: 3 },
        { name: t`Products`, isSynced: false, viewOrder: 2, synOrder: 4 },
        { name: t`Product Groups`, isSynced: false, viewOrder: 5, synOrder: 5 },
        { name: t`Units`, isSynced: false, viewOrder: 8, synOrder: 6 },
        { name: t`Suppliers`, isSynced: false, viewOrder: 3, synOrder: 7 },
        { name: t`Supplier Groups`, isSynced: false, viewOrder: 6, synOrder: 8 },
        { name: t`More...`, isSynced: false, viewOrder: 9, synOrder: 9, isLast: true },
    ];

    const delay = (ms: number): Promise<void> =>
        new Promise<void>(resolve => {
            setTimeout(resolve, ms);
        });

    const isDataSynchronizationCompleted = async (): Promise<boolean> => {
        const lastSyncDataItem = syncDataItems.find(p => p.isLast);
        if (lastSyncDataItem?.isLast && lastSyncDataItem.syncInProgress) {
            const resp = await internalApiClient.get<boolean>(`/rackbeat-lager/company/is-data-synchronization-completed`);
            return resp.data;
        }
        return false;
    };

    // Polling for 1 min before showing warning
    const [, setNumberOfRetriesRemaining] = useState(12);
    const [syncDataItems, setSyncDataItems] = useState(initialSyncDataItems);
    const [showProgressTakingLong, setShowProgressTakingLong] = useState(false);
    const [isDataSyncCompleted, setIsDataSyncCompleted] = useState(false);
    const [startTime, setStartTime] = useState(0);
    const intervalRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const simulateDataSync = async () => {
        const batchSize = 3;
        const sortedSyncDataItems = syncDataItems.filter(item => !item.isLast).sort((a, b) => a.synOrder - b.synOrder);

        for (let i = 0; i < sortedSyncDataItems.length; i += batchSize) {
            const batch = sortedSyncDataItems.slice(i, i + batchSize).map(item => item.name);

            setSyncDataItems(prevItems =>
                prevItems.map(item => (batch.includes(item.name) ? { ...item, syncInProgress: true } : item))
            );

            await delay(2000);

            setSyncDataItems(prevItems =>
                prevItems.map(item => (batch.includes(item.name) ? { ...item, syncInProgress: false, isSynced: true } : item))
            );
        }

        // Set last item to be progressing
        setSyncDataItems(prevItems => prevItems.map(item => (item.isLast ? { ...item, syncInProgress: true } : item)));
    };

    const checkDataSync = async () => {
        const lastSyncCheckSuccessful = await isDataSynchronizationCompleted();
        setNumberOfRetriesRemaining(prev => {
            const newRetries = prev - 1;
            if (lastSyncCheckSuccessful || isDataSyncCompleted) {
                trackEvent('total-data-sync-duration', { duration: (performance.now() - startTime) / 1000 });

                setSyncDataItems(prevItems => prevItems.map(item => (item.isLast ? { ...item, isSynced: true } : item)));

                setTimeout(() => {
                    clearInterval(intervalRef.current);
                    handleOnAcceptTransfer();
                }, 1000);
            } else if (newRetries < 0 && !showProgressTakingLong) {
                setShowProgressTakingLong(true);
            }
            return newRetries;
        });
    };

    useEffect(() => {
        setStartTime(performance.now());

        simulateDataSync();
    }, []);

    useEffect(() => {
        intervalRef.current = setInterval(checkDataSync, 5000);

        return () => clearInterval(intervalRef.current);
    }, [syncDataItems, isDataSyncCompleted, handleOnAcceptTransfer, showProgressTakingLong]);

    const onAcceptTransferClicked = () => {
        trackEvent('total-data-sync-duration', { duration: (performance.now() - startTime) / 1000 });

        setIsDataSyncCompleted(true);
        handleOnAcceptTransfer();
    };

    const getProgressStatus = (syncItem: SyncDataItem) => {
        if (!syncItem.syncInProgress && !syncItem.isSynced) {
            return ProgressStatus.NotStarted;
        }
        if (syncItem.syncInProgress) {
            return ProgressStatus.InProgress;
        }
        return ProgressStatus.Completed;
    };

    const enum ProgressStatus {
        NotStarted,
        InProgress,
        Completed,
    }

    return (
        <>
            <div className="mb-2">{children}</div>
            <h2 className="text-center">
                <Trans>Transfer in progress</Trans>
            </h2>
            {showProgressTakingLong && (
                <Alert state="warning" title="">
                    <Trans>
                        Even if the transfer of your data takes longer than expected, you can proceed to the next step. We will
                        complete the transfer and send an email when it is finished.
                    </Trans>
                </Alert>
            )}
            <div className="mb-4 mt-4 whitespace-nowrap">
                <div className="grid w-full grid-cols-3 gap-2">
                    <div>
                        <Icon name="person-tick" />
                    </div>
                    <div>
                        <Icon name="tag" />
                    </div>
                    <div>
                        <Icon name="webshop" />
                    </div>
                    {syncDataItems
                        .sort((a, b) => a.viewOrder - b.viewOrder)
                        .map(item => {
                            const progressStatus = getProgressStatus(item);
                            return (
                                <div
                                    key={item.name}
                                    className={`flex items-center ${
                                        progressStatus === ProgressStatus.NotStarted ? 'opacity-30' : 'opacity-100'
                                    } 
                                transition-opacity duration-1000 ease-in-out`}>
                                    <span className="inline-block w-6 h-6 overflow-hidden">
                                        {progressStatus === ProgressStatus.NotStarted && (
                                            <Icon name="line" className="text-blue-700" />
                                        )}
                                        {progressStatus === ProgressStatus.InProgress && (
                                            <Spinner style={{ width: '18px', height: '18px' }} />
                                        )}
                                        {progressStatus === ProgressStatus.Completed && (
                                            <Icon name="tick" className="text-blue-700" />
                                        )}
                                    </span>
                                    <span>{item.name}</span>
                                </div>
                            );
                        })}
                </div>
            </div>
            {showProgressTakingLong && (
                <div className="text-right mt-4">
                    <Button appearance="primary" onClick={onAcceptTransferClicked}>
                        <Trans>Okay, proceed</Trans>
                    </Button>
                </div>
            )}
        </>
    );
}
